import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { ContactList } from '../../../../../js/api';
import { HealthcareEntitiesService } from '../../../../../js/api';
import { SessionContext, SessionContextType } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ConfirmationDialog } from '../../../components/confirmation-dialog';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { BooleanFilterConfig, ListFilter } from '../../../components/list-filter';
import { useBulkRowSelection } from '../../../hooks/useBulkRowSelection';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import { formatContactName, formatContactSortName, pluralize } from './contactDisplay';
import { ContactDrawer } from './ContactDrawer';
import {
  CONTACT_DELETE_PERMISSION,
  LINK_CREATE_PERMISSION,
  LINK_DELETE_PERMISSION,
} from './contactPermissions';
import { ContactsBulkBar } from './ContactsBulkBar';
import { ContactRow, ContactsResults } from './ContactsResults';
import { LinkContactsDialog } from './LinkContactsDialog';
import { contactQueryKeys, invalidateContactQueries } from './queryKeys';
import { CONTACT_CUSTOM_FILTERS, useContactFacilityFilter } from './useContactFacilityFilter';

const PAGE_SIZE = 21;
const ROLE_CATEGORY = 'FacilityContactRoles';
const CATEGORIES: string[] = [ROLE_CATEGORY];
const BOOLEAN_FILTERS: BooleanFilterConfig[] = [
  {
    queryString: 'unlinked',
    trueLabel: 'No facilities',
    falseLabel: '',
    renderAs: 'checkbox',
    defaultValue: false,
  },
];

type RoleCode = NonNullable<
  Parameters<typeof HealthcareEntitiesService.healthcareEntitiesContactsList>[0]['role']
>[number];

export type ContactsListPageProps = {
  title?: string;
  queryKeyBase: string;
  displayTypePrefKey: string;
  showOrganization: boolean;
  showOrganizationFilter?: boolean;
  /** Row checkboxes + bulk bar (staff only for now). */
  enableBulkActions?: boolean;
};

const hasId = (contact: ContactList): contact is ContactRow => Boolean(contact.id);
const ContactsListState: React.FC<{ isError: boolean }> = ({ isError }) =>
  isError ? (
    <ListErrorState testId="contacts-error" />
  ) : (
    <div className="tw:col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
      No contacts found.
      <div className="tw:text-zinc-500 tw:italic tw:text-sm">Refine your search or filters.</div>
    </div>
  );

/** The one selected contact's name (for the link dialog title), else undefined. */
const nameOfSingleSelected = (contacts: ContactRow[], selectedIds: string[]) => {
  if (selectedIds.length !== 1) return undefined;
  const contact = contacts.find((row) => row.id === selectedIds[0]);
  return contact ? formatContactName(contact) : undefined;
};

const getContactEntityId = (contact: ContactRow) => contact.healthcare_entity;

export const ContactsListPage: React.FC<ContactsListPageProps> = ({
  title = 'Contacts',
  queryKeyBase,
  displayTypePrefKey,
  showOrganization,
  showOrganizationFilter = false,
  enableBulkActions = false,
}) => {
  useDocumentTitle(title);
  const queryClient = useQueryClient();
  const { audienceIsStaff } = useContext<SessionContextType>(SessionContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const canDelete = useHasPermission(CONTACT_DELETE_PERMISSION);
  const canLink = useHasPermission(LINK_CREATE_PERMISSION);
  const canUnlink = useHasPermission(LINK_DELETE_PERMISSION);
  const bulkEnabled = enableBulkActions && (canDelete || canLink || canUnlink);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [linkMode, setLinkMode] = useState<'add' | 'remove' | null>(null);

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
    booleanFilters: BOOLEAN_FILTERS,
    customFilters: CONTACT_CUSTOM_FILTERS,
  });

  const search = filterValues.search || '';
  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  const ordering = (filterValues.ordering || 'last_name') as string;
  const healthcareEntity = filterValues.healthcare_entity || '';
  const roles = useMemo(
    () => (filterValues.categories?.[ROLE_CATEGORY] || []) as RoleCode[],
    [filterValues.categories]
  );
  const unlinked = filterValues.booleanFilters?.unlinked === true;

  const setOrdering = useCallback(
    (next: string) => updateFilters({ ...filterValues, displayType, ordering: next }),
    [updateFilters, filterValues, displayType]
  );

  const { facilityIds, customFilters } = useContactFacilityFilter({
    healthcareEntity,
    audienceIsStaff: Boolean(audienceIsStaff),
    filterValues,
    displayType,
    updateFilters,
  });

  const listParams = useMemo(
    () => ({ search, healthcareEntity, roles, facilityIds, unlinked, ordering }),
    [search, healthcareEntity, roles, facilityIds, unlinked, ordering]
  );

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: contactQueryKeys.list(queryKeyBase, listParams),
      queryFn: async ({ pageParam = 0 }) =>
        HealthcareEntitiesService.healthcareEntitiesContactsList({
          search: search || undefined,
          healthcareEntity: healthcareEntity || undefined,
          role: roles.length > 0 ? roles : undefined,
          facility: facilityIds.length > 0 ? facilityIds : undefined,
          unlinked: unlinked || undefined,
          ordering,
          limit: PAGE_SIZE,
          offset: pageParam as number,
        }),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.next ? allPages.length * PAGE_SIZE : undefined,
    });

  const contacts = useMemo(
    () => (data?.pages.flatMap((page) => page.results || []) || []).filter(hasId),
    [data]
  );
  const totalCount = data?.pages[0]?.count || 0;

  const loadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const { loadingRef } = useInfiniteScroll(loadMore, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const selection = useBulkRowSelection<ContactRow>({
    rows: contacts,
    enabled: bulkEnabled,
    resetKey: JSON.stringify(listParams),
    getEntityId: getContactEntityId,
    getLabel: formatContactSortName,
    testIdPrefix: 'contacts',
  });

  const singleSelectedName = nameOfSingleSelected(contacts, selection.selectedIds);

  const selectedIdFromUrl = searchParams.get('id');
  const isDrawerOpen = Boolean(selectedIdFromUrl);

  const handleRowClick = useCallback(
    (contactId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('id', contactId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) =>
      HealthcareEntitiesService.healthcareEntitiesContactsBulkDeleteCreate({
        requestBody: { ids },
      }),
    onSuccess: async (result, ids) => {
      const skipped = result.skipped ? ` · ${result.skipped} skipped` : '';
      toast.success(`Deleted ${pluralize(result.processed, 'contact')}${skipped}`);
      setIsDeleteOpen(false);
      selection.clear();
      if (selectedIdFromUrl && ids.includes(selectedIdFromUrl)) {
        handleCloseDrawer();
      }
      await invalidateContactQueries(queryClient);
    },
    onError: (error) => handleApiError(error, { messagePrefix: 'Failed to delete contacts' }),
  });

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0">
                  <i className="far fa-fw fa-address-book tw:mr-1" />
                  {title}
                </Heading>
              </div>
              <div className="tw:mt-1 tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  categories={CATEGORIES}
                  labelOverrides={{ [ROLE_CATEGORY]: 'Role' }}
                  showStatus={false}
                  showReason={false}
                  showOrganizationFilter={showOrganizationFilter}
                  showDisplayType={true}
                  booleanFilters={BOOLEAN_FILTERS}
                  customFilters={customFilters}
                  sortOptions={[
                    { label: 'Name', value: 'last_name' },
                    { label: 'Email', value: 'email' },
                    ...(showOrganization
                      ? [{ label: 'Organization', value: 'healthcare_entity__name' }]
                      : []),
                    { label: 'Facilities', value: 'facility_count' },
                    { label: 'Last Updated', value: 'updated_at' },
                  ]}
                  values={filterValues}
                  onFilterChange={updateFilters}
                />
              </div>
            </div>
            <div className="tw:mt-3">
              {isLoading && contacts.length === 0 && (
                <div className="tw:text-center tw:py-8">Loading contacts...</div>
              )}
              {isError || (!isLoading && contacts.length === 0) ? (
                <ContactsListState isError={isError} />
              ) : (
                <>
                  {contacts.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {contacts.length} of {totalCount} contacts
                    </div>
                  )}

                  <ContactsResults
                    contacts={contacts}
                    displayType={displayType}
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                    showOrganization={showOrganization}
                    selectedId={selectedIdFromUrl}
                    onOpen={handleRowClick}
                    renderSelectControl={selection.renderRowControl}
                    headerSelectControl={selection.headerControl}
                  />

                  {hasNextPage && (
                    <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                      {isFetchingNextPage && (
                        <div className="tw:text-center tw:text-zinc-500">
                          Loading more contacts...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            {bulkEnabled && (
              <ContactsBulkBar
                count={selection.count}
                busy={deleteMutation.isLoading}
                canLink={canLink}
                canUnlink={canUnlink}
                canDelete={canDelete}
                isActingAs={Boolean(selection.actingAsEntity)}
                onAdd={() => setLinkMode('add')}
                onRemove={() => setLinkMode('remove')}
                onDelete={() => setIsDeleteOpen(true)}
                onClear={selection.clear}
              />
            )}
          </main>
        </div>

        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedIdFromUrl && (
              <ContactDrawer
                key={selectedIdFromUrl}
                contactId={selectedIdFromUrl}
                onClose={handleCloseDrawer}
                showOrganization={showOrganization}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>

      <ConfirmationDialog
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate(selection.selectedIds)}
        title={`Delete ${pluralize(selection.count, 'contact')}?`}
        body="They will be removed from all of their facilities and stop receiving notifications for them."
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isLoading}
      />
      <LinkContactsDialog
        open={linkMode !== null}
        onClose={() => setLinkMode(null)}
        mode={linkMode ?? 'add'}
        direction="contactsToFacilities"
        contactIds={selection.selectedIds}
        healthcareEntityId={selection.actingAsEntity?.id}
        subjectName={singleSelectedName}
        onDone={selection.clear}
      />
    </>
  );
};
